import {
  ChartBar,
  Code,
  Cpu,
  CreditCard,
  DeviceMobile,
  House,
  IconProps,
  Lifebuoy,
  Lightbulb,
  TreeStructure,
  UserCircle,
  UsersFour,
} from '@phosphor-icons/react';
import { Loading } from '@whistler-ui/k2/components/Loading';
import UnderReviewPage from '@whistler-ui/k2/components/UnderReviewPage';
import { USE_CASE_NAMES } from '@whistler-ui/k2/constants/websheetsConstants';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import { PageRoute } from '@whistler-ui/k2/types/PageRoute';
import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router';

import { BridgeKeeper } from '../components/BridgeKeeper';
import { SETTINGS } from '../constants/settings';
import SignIn from '../pages/Authentication/SignIn';
import { signOut } from '../services/helper';
import { useAuthStore } from '../store';
import { useThemeStore } from '../store/theme';
import PrivateGuard from './PrivateGuard';
import PublicGuard from './PublicGuard';
import Root from './Root';

const LazyNotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const LazyStatusPage = lazy(() => import('../pages/UptimeStatusPage'));

// Local components
const RedirectToDashboard = () => (
  <Navigate to={routes.dashboard.url} replace />
);

const BKStatusPage = () => {
  return (
    <BridgeKeeper
      feature="health-status"
      withLoader
      fallbackComponent={RedirectToDashboard}
    >
      <LazyStatusPage />
    </BridgeKeeper>
  );
};

const UnderReview = () => {
  const { config } = useThemeStore();
  const bgImage = config.assets.reviewBgPattern;

  return (
    <UnderReviewPage
      signOut={signOut}
      logoUrl={config.assets.logo}
      bgImage={bgImage}
    />
  );
};

/**
 * Menu items.
 *
 * Have an icon and the route, optionally a boolean indicating if the item
 * should be hidden.
 */
const routes: Record<string, PageRoute> = {
  basePath: {
    url: '/',
  },
  signup: {
    url: '/signup',
    public: true,
    exact: true,
    component: lazy(() => import('../pages/Authentication/SignUp')),
  },
  login: {
    url: '/login',
    public: true,
    exact: true,
    component: SignIn,
  },
  signupTempPassword: {
    url: '/signup/temp-password',
    component: lazy(() => import('../pages/Authentication/TempPassword')),
    public: true,
    exact: true,
  },
  changePassword: {
    url: '/login/change-password',
    component: lazy(() => import('../pages/Authentication/ChangePassword')),
    public: true,
  },
  forgotPassword: {
    url: '/login/forgot-password',
    component: lazy(() => import('../pages/Authentication/ForgotPassword')),
    public: true,
  },
  mfaSetup: {
    url: '/login/mfaSetup',
    exact: true,
    component: lazy(() => import('../pages/Authentication/MFASetup')),
    public: true,
  },
  totp: {
    url: '/login/totp',
    public: true,
    exact: true,
    component: lazy(() => import('../pages/Authentication/TOTP')),
  },
  trustDevice: {
    url: '/login/trust-device',
    component: lazy(() => import('../pages/Authentication/TrustDevice')),
    public: true,
  },
  codex: {
    url: '/codex/*',
    displayName: 'Codex',
    icon: Cpu,
    showInMenu: true,
    order: 12,
    component: lazy(() => import('../pages/Codex')),
    showOnlyOnDevMode: true,
  },
  dashboard: {
    url: '/dashboard',
    displayName: 'route_display_name_dashboard',
    icon: House,
    showInMenu: true,
    order: 2,
    component: lazy(() => import('../pages/Dashboard')),
  },
  devices: {
    url: '/devices',
    displayName: 'route_display_name_devices',
    component: lazy(() => import('../modules/DevicesLog')),
    icon: DeviceMobile,
    showInMenu: true,
    permission: 'access-to-devices',
    order: 4,
  },
  myTeam: {
    url: '/my-team',
    displayName: 'route_display_name_my_team',
    component: lazy(() => import('../pages/MyTeam')),
    icon: UsersFour,
    showInMenu: true,
    permission: 'access-to-my-team',
    order: 5,
  },
  reports: {
    url: '/reports',
    displayName: 'route_display_name_reports',
    component: lazy(() => import('../modules/Reports')),
    icon: ChartBar,
    showInMenu: true,
    permission: 'access-to-reports',
    order: 6,
  },
  billing: {
    url: '/billing',
    displayName: 'route_display_name_billing',
    component: lazy(() => import('../pages/BillingPage')),
    icon: CreditCard,
    showInMenu: true,
    permission: 'access-to-billing',
    order: 7,
  },
  developers: {
    url: '/developers',
    component: lazy(() => import('../pages/Developers')),
    displayName: 'route_display_name_developers',
    icon: Code,
    showInMenu: true,
    permission: 'access-default-actions',
    order: 8,
  },
  notification: {
    url: '/notification',
    displayName: 'route_display_name_notification',
    component: lazy(() => import('../pages/NotificationPage')),
  },
  support: {
    url: '/support',
    displayName: 'route_display_name_support',
    icon: React.forwardRef<SVGSVGElement, IconProps>((props, ref) => (
      <Lifebuoy ref={ref} {...props} />
    )),
    showInMenu: true,
    permission: 'access-to-support',
    order: 9,
    component: lazy(() => import('../pages/SupportPage')),
  },
  editAccount: {
    url: '/profile',
    displayName: 'route_display_name_profile',
    component: lazy(() => import('../pages/AccountPage')),
    icon: UserCircle,
    showInMenu: true,
    order: 10,
  },
  status: {
    url: '/status',
    isBlankPage: true,
    isStandAlone: true,
    displayName: 'route_display_name_health_status',
    component: BKStatusPage,
  },
  account: {
    url: '/account',
    displayName: 'route_display_name_account',
    icon: Lightbulb,
    permission: 'access-to-upgrade',
    order: 11,
    showInMenu: true,
    component: lazy(() => import('../pages/UpgradePage')),
  },
  underReview: {
    url: '/under-review',
    displayName: 'route_display_name_account_under_review',
    component: UnderReview,
    isBlankPage: true,
  },
  entitlements: {
    url: '/entitlements',
    exact: true,
    displayName: 'route_display_name_entitlements',
    showInMenu: true,
    permission: 'access-default-actions',
    useTopPositionClassName: 'entitlements',
    order: 3,
    icon: TreeStructure,
    subMenu: true,
    subMenuOrder: 1,
    subMenuOrderDisplayName: 'route_display_name_use_cases',
    component: lazy(() => import('../pages/Entitlements/EntitlementsPage')),
  },
  entitlementsImplement: {
    url: '/entitlements/implement',
    displayName: 'route_display_name_implement',
    permission: 'access-default-actions',
    useTopPositionClassName: 'entitlements-api-implementation',
    subMenu: true,
    subMenuOrder: 2,
    component: lazy(
      () => import('../pages/Entitlements/modules/EntitlementsImplementation'),
    ),
  },
  entitlementsTest: {
    url: '/entitlements/test/integration',
    displayName: 'route_display_name_api_testing',
    permission: 'access-default-actions',
    component: lazy(
      () =>
        import(
          '../pages/Entitlements/modules/EntitlementsApiTestExecution/index'
        ),
    ),
    subMenu: true,
    subMenuOrder: 3,
  },
  entitlementsTestUseCase: {
    url: '/entitlements/test/use-case',
    displayName: 'route_display_name_uc_testing',
    permission: 'access-default-actions',
    component: lazy(
      () =>
        import(
          '../pages/Entitlements/modules/EntitlementsUseCaseTestExecution/index'
        ),
    ),
    subMenu: true,
    subMenuOrder: 4,
  },
  entitlementsTestVoLTE: {
    url: '/entitlements/test/volte_default',
    displayName: 'route_display_name_VoLTE',
    permission: 'access-default-actions',
    useCases: [USE_CASE_NAMES.VOLTE_DEFAULT_ON],
    component: lazy(() => import('../pages/Entitlements/modules/VoLTE')),
    subMenu: true,
    subMenuOrder: 5,
  },
  entitlementsProduction: {
    url: '/entitlements/production/integration',
    permission: 'access-default-actions',
    displayName: 'route_display_name_production',
    component: UnderReview,
  },
  privacyPolicy: {
    url: '/privacy-policy',
    public: true,
    displayName: 'route_display_name_privacy_policy',
    allowAccessAfterLogin: true,
    allowedPartners: ['GD'],
    component: lazy(() => import('../pages/PrivacyPolicyPage')),
  },
  cookiePolicy: {
    url: '/cookie-policy',
    public: true,
    displayName: 'route_display_name_cookie_policy',
    allowAccessAfterLogin: true,
    component: lazy(() => import('../pages/CookiePolicyPage')),
  },
  not_found: {
    url: '*',
    public: true,
    component: LazyNotFoundPage,
  },
  iframeAuthTest: {
    url: '/iframe-auth-test',
    public: true,
    showOnlyOnDevMode: true,
    displayName: 'IFrame Auth Test',
    component: lazy(
      () => import('../pages/IframeAuthTestPage/IFrameAuthTestPage'),
    ),
  },
};

const filterProductionModeRoutes = ([, route]) => {
  return !route.hiddenOnProductionMode;
};

const filterTestModeRoutes = ([, route]) => {
  return !route.hiddenOnTestMode;
};

// Dev-only routes (e.g. Codex) are shown when running the local dev server
// (import.meta.env.DEV) or when deployed to the ci/dev environments. This keeps
// them out of production while not depending on a specific Sentry env string in
// local development.
const filterRoutesByDevMode = () => {
  const allowedEnvs = ['portal-ci-netlync', 'portal-dev-netlync'];
  const isDevLike =
    import.meta.env.DEV || allowedEnvs.includes(SETTINGS.REACT_APP_SENTRY_ENV);

  return ([, route]) => {
    if (route.showOnlyOnDevMode && !isDevLike) {
      return false;
    }
    return true;
  };
};

export const routeFilters = {
  filterTestModeRoutes,
  filterProductionModeRoutes,
  customFilters: [],
};

const getRoutes = (
  isValidProductionUpgrade: boolean | undefined,
  config: any,
) => {
  // Filter routes based on the mode (production or test) and development mode
  const filteredRoutes = Object.entries(routes)
    .filter(
      isValidProductionUpgrade
        ? filterProductionModeRoutes
        : filterTestModeRoutes,
    )
    .filter(filterRoutesByDevMode());

  // Use reduce to accumulate public and private routes
  return filteredRoutes.reduce(
    (acc, [_key, route]) => {
      // Check if the route has allowedPartners and if the companyFlag is included
      if (
        route.allowedPartners &&
        !route.allowedPartners.includes(
          config.environments.settings.companyFlag,
        )
      ) {
        return acc; // Skip this route by returning the accumulator unchanged
      }

      // Skip routes without a valid component
      if (!route.component) {
        return acc;
      }

      // Create a route object with the necessary properties
      const routeObject = {
        path: route.url,
        element: <route.component />,
      };

      // Add to publicRoutes or privateRoutes based on route.public
      if (route.public) {
        if (route.allowAccessAfterLogin) {
          acc.afterLoginRoutes.push(routeObject);
        } else {
          acc.publicRoutes.push(routeObject);
        }
      } else {
        acc.privateRoutes.push(routeObject);
      }

      return acc;
    },
    // Initial value: object with empty arrays for public and private routes
    { publicRoutes: [], privateRoutes: [], afterLoginRoutes: [] },
  );
};

const Routes = () => {
  const { t } = useTranslation();
  useAppMessages(t);

  const {
    authValues: {
      organisationState: { isValidProductionUpgrade },
    },
  } = useAuthStore();

  const { config } = useThemeStore();

  const { publicRoutes, privateRoutes, afterLoginRoutes } = getRoutes(
    isValidProductionUpgrade,
    config,
  );

  const { pathname } = useLocation();

  const currentRouteConfig = Object.values(routes).find(
    route => route.url === pathname,
  ) satisfies PageRoute;

  return (
    <Suspense fallback={<Loading.Overlay />}>
      {useRoutes([
        {
          path: routes.basePath.url,
          element: <Root />,
          children: [
            {
              element: (
                <PrivateGuard
                  isBlankPage={currentRouteConfig?.isBlankPage}
                  isStandAlone={currentRouteConfig?.isStandAlone}
                  routeFilters={routeFilters}
                  {...currentRouteConfig}
                />
              ),
              children: [...privateRoutes],
            },
            {
              element: <PublicGuard />,
              children: [...publicRoutes],
            },
            {
              element: <Outlet />,
              children: [...afterLoginRoutes],
            },
          ],
        },
        // Catch-all route for 404
        {
          path: '*',
          element: <LazyNotFoundPage />,
        },
      ])}
    </Suspense>
  );
};

export { routes };
export default Routes;
